import { useCallback, useEffect, useRef, useState } from "react";
import { addMonths, format, isSameDay, parse } from "date-fns";

import { env } from "~/env";
import { useGetProvidersSlots } from "~/screens/Scheduling/InitialVisit/hooks/use-get-providers-slots";
import { useIpInfo } from "~/screens/Scheduling/InitialVisit/hooks/useIpInfo";
import { useTrackSlotDateViews } from "~/screens/Scheduling/InitialVisit/hooks/useTrackSlotDateViews";
import {
  formatAppointmentTime,
  TIMEZONES,
} from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import { getDefaultTimeZone } from "~/services/ipService";
import { VISIT_TYPES } from "~/services/scheduling/constants";
import type { VisitType } from "~/services/scheduling/types";
import type { CalendarDay, TimeSlot } from "~/shared.types";
import { isSelectableDay } from "~/shared/utils/day-availability";
import { formatDateForAPI } from "~/shared/utils/format-date-for-api";
import { formatDateTimeForAPI } from "~/shared/utils/format-datetime-for-api";
import { generateCalendarDaysForMonth } from "~/shared/utils/generate-calendar-days-for-month";
import { getFirstBusinessDayOfMonth } from "~/shared/utils/get-first-business-day-of-month";
import { getFirstDayWithSlots } from "~/shared/utils/get-first-day";
import { handleMonthChange } from "~/shared/utils/month-change-handler";
import type { AppointmentDetails } from "~/stores/use-patient-store";
import { useSchedulingTimezoneStore } from "~/stores/use-scheduling-timezone-store";

type MonthYear = { month: string; year: number };

type UseStaffSlotsParams = {
  visitType: VisitType;
  stateId: number;
  providerId?: number | null;
  insuranceId?: number | null;
  initialSelection?: AppointmentDetails | null;
};

const getMaxAllowedDate = () => {
  const today = new Date();

  return new Date(
    today.getFullYear(),
    today.getMonth() + Number(env.VITE_APPOINTMENT_SLOT_MAX_MONTHS),
    1,
  );
};

const getInitialCalendar = (initialSelection?: AppointmentDetails | null) => {
  const today = new Date();
  const restoredDate = initialSelection?.timeSlot
    ? parse(initialSelection.formattedDate, "MM/dd/yyyy", today)
    : null;
  const monthDate = restoredDate ?? today;
  const isCurrentMonth =
    monthDate.getFullYear() === today.getFullYear() &&
    monthDate.getMonth() === today.getMonth();
  const calendarDays = generateCalendarDaysForMonth(
    monthDate.getFullYear(),
    monthDate.getMonth(),
  );
  const restoredDay = restoredDate
    ? calendarDays.find((day) => isSameDay(day.date, restoredDate))
    : undefined;

  return {
    monthYear: {
      month: monthDate.toLocaleDateString("en-US", { month: "long" }),
      year: monthDate.getFullYear(),
    },
    startDate: isCurrentMonth
      ? format(today, "MM/dd/yyyy")
      : formatDateForAPI(
          getFirstBusinessDayOfMonth(
            monthDate.getFullYear(),
            monthDate.getMonth(),
          ),
        ),
    calendarDays,
    selectedDay:
      restoredDay ??
      calendarDays.find((day) => day.isToday && day.isAvailable) ??
      calendarDays.find((day) => day.isAvailable) ??
      calendarDays[0],
    timeSlot: restoredDay ? (initialSelection?.timeSlot ?? null) : null,
    providerId: restoredDay ? (initialSelection?.provider?.id ?? null) : null,
  };
};

export const useStaffSlots = ({
  visitType,
  stateId,
  providerId,
  insuranceId,
  initialSelection,
}: UseStaffSlotsParams) => {
  const [maxAllowedDate] = useState(getMaxAllowedDate);
  const [initialCalendar] = useState(() =>
    getInitialCalendar(initialSelection),
  );
  const isRestoringSelection = !!initialCalendar.timeSlot;

  const [currentMonthYear, setCurrentMonthYear] = useState<MonthYear>(
    initialCalendar.monthYear,
  );
  const [currentStartDate, setCurrentStartDate] = useState(
    initialCalendar.startDate,
  );
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>(
    initialCalendar.calendarDays,
  );
  const [selectedDay, setSelectedDay] = useState<CalendarDay>(
    initialCalendar.selectedDay,
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    initialCalendar.timeSlot,
  );
  const [selectedProvider, setSelectedProvider] = useState<number | null>(
    initialCalendar.providerId,
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hasNoAvailability, setHasNoAvailability] = useState(false);

  const { selectedTimeZone, setSelectedTimeZone } =
    useSchedulingTimezoneStore();
  const { data: ipInfo, isPending: isPendingIpInfo } = useIpInfo();
  const hasResolvedDefaultTimeZone = useRef(false);
  const userWentBack = useRef(false);
  const autoSelectedStartDate = useRef<string | null>(
    isRestoringSelection ? initialCalendar.startDate : null,
  );
  const isSearchingFirstAvailability = useRef(!isRestoringSelection);

  const isFollowUp = visitType === VISIT_TYPES.FOLLOW_UP;
  const filteredProviderId = isFollowUp && providerId ? providerId : undefined;

  const {
    data: appointmentSlotsByProvider,
    isPending,
    isError,
    refetch,
  } = useGetProvidersSlots({
    params: {
      stateId,
      appointmentType: visitType,
      startDate: currentStartDate,
      dateRange: "current_month",
      providerId: filteredProviderId,
      insuranceId,
      isAdmin: true,
    },
  });

  useTrackSlotDateViews({
    date: selectedDay.date,
    providers: appointmentSlotsByProvider ?? [],
    enabled: !isPending,
  });

  const hasAppointments = useCallback(
    (date: Date) =>
      isSelectableDay({
        date,
        appointmentSlotsByProvider,
        isProviderView: true,
        providerId: filteredProviderId,
      }),
    [appointmentSlotsByProvider, filteredProviderId],
  );

  const changeMonth = (monthYear: MonthYear) =>
    handleMonthChange({
      monthYear,
      hasAppointments,
      setCurrentMonthYear,
      setCurrentStartDate,
      setCalendarDays,
      setSelectedDay,
      updateUrl: false,
    });

  useEffect(() => {
    if (
      isPending ||
      isError ||
      autoSelectedStartDate.current === currentStartDate
    )
      return;

    autoSelectedStartDate.current = currentStartDate;

    const firstDayWithSlots = getFirstDayWithSlots({
      days: calendarDays,
      appointmentSlotsByProvider,
      isProviderView: true,
    });

    if (firstDayWithSlots) {
      isSearchingFirstAvailability.current = false;
      setSelectedDay(firstDayWithSlots);
      return;
    }

    if (userWentBack.current) {
      userWentBack.current = false;
      return;
    }

    const firstCalendarDay = calendarDays[0];
    if (!firstCalendarDay) return;

    const nextMonth = addMonths(firstCalendarDay.date, 1);
    if (nextMonth > maxAllowedDate) {
      setHasNoAvailability(isSearchingFirstAvailability.current);
      return;
    }

    void changeMonth({
      month: nextMonth.toLocaleString("en-US", { month: "long" }),
      year: nextMonth.getFullYear(),
    });
  }, [appointmentSlotsByProvider, isPending, isError]);

  useEffect(() => {
    if (hasResolvedDefaultTimeZone.current || isPendingIpInfo) return;

    hasResolvedDefaultTimeZone.current = true;
    const defaultTimeZoneCode = getDefaultTimeZone(ipInfo ?? null);
    setSelectedTimeZone(
      TIMEZONES[defaultTimeZoneCode as keyof typeof TIMEZONES] ?? TIMEZONES.ET,
    );
  }, [ipInfo, isPendingIpInfo, setSelectedTimeZone]);

  const onMonthChange = async (monthYear: MonthYear) => {
    const newDate = new Date(`${monthYear.month} 1, ${monthYear.year}`);
    if (newDate > maxAllowedDate) return;

    const currentMonthDate = new Date(
      `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
    );
    userWentBack.current = newDate < currentMonthDate;
    isSearchingFirstAvailability.current = false;
    setSelectedTimeSlot(null);
    setSelectedProvider(null);

    await changeMonth(monthYear);
  };

  const onDaySelect = (day: CalendarDay) => {
    setSelectedDay(day);
    setSelectedTimeSlot(null);
    setSelectedProvider(null);
    setIsCalendarOpen(false);
  };

  const onTimeSlotSelect = (timeSlot: TimeSlot, slotProviderId?: number) => {
    setSelectedTimeSlot(timeSlot);
    setSelectedProvider(slotProviderId ?? null);
  };

  const getAppointmentDetails = (): AppointmentDetails | null => {
    if (!selectedTimeSlot || !selectedProvider) return null;

    const provider = appointmentSlotsByProvider?.find(
      (option) => option.id === selectedProvider,
    ) ?? {
      id: selectedProvider,
      name: selectedTimeSlot.providerName ?? "",
      title: selectedTimeSlot.providerTitle ?? "",
      slots: {},
      firstName: undefined,
      lastName: undefined,
      carePhilosophy: undefined,
      additionalExpertise: [],
      languages: [],
      testimonials: [],
      yearsOfExperience: undefined,
    };
    const appointmentDate = format(selectedDay.date, "MM/dd/yyyy");

    return {
      provider,
      timeSlot: selectedTimeSlot,
      timezone: selectedTimeZone.code,
      date: format(selectedDay.date, "EEEE, MMMM d"),
      formattedDate: appointmentDate,
      formattedTime: formatAppointmentTime(
        selectedTimeSlot.startTime,
        selectedTimeSlot.duration,
        selectedTimeZone,
      ),
      visitDateTime: formatDateTimeForAPI(
        appointmentDate,
        selectedTimeSlot.originalTime ?? "",
      ),
    };
  };

  return {
    calendar: {
      selectedDay,
      calendarDays,
      monthYear: currentMonthYear,
      maxAllowedDate,
      isCalendarOpen,
      hasAppointments,
    },
    selection: { selectedTimeSlot, selectedProvider },
    handlers: {
      onDaySelect,
      onMonthChange,
      onOpenCalendar: () => setIsCalendarOpen(true),
      onCloseCalendar: () => setIsCalendarOpen(false),
      onTimeSlotSelect,
    },
    appointmentSlotsByProvider: appointmentSlotsByProvider ?? [],
    isPending,
    isError,
    retry: () => void refetch(),
    hasNoAvailability: hasNoAvailability && !isPending && !isError,
    getAppointmentDetails,
  };
};
